import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, withCtx as _withCtx, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "center-container" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 0,
  role: "status",
  class: "sr-only"
}

import { ref, computed, onBeforeMount, watch } from 'vue';

// Personal components
import QPageTitle from '@components/QPageTitle.vue';
import DialogUserRoles from '@components/dialog/user/DialogUserRoles.vue';
import DialogUserCreate from '@components/dialog/user/DialogUserCreate.vue';
import DialogUserUpdate from '@components/dialog/user/DialogUserUpdate.vue';
import DialogUserDelete from '@components/dialog/user/DialogUserDelete.vue';
import DialogUserAccept from '@components/dialog/user/DialogUserAccept.vue';
import DialogUserReject from '@components/dialog/user/DialogUserReject.vue';
import DialogUserNewPassword from '@components/dialog/user/DialogUserNewPassword.vue';
import DialogUserResendValidation from '@components/dialog/user/DialogUserResendValidation.vue';
import QAdminUserList from '@pages/admin/components/QAdminUserList.vue';
import QOfflineBanner from 'src/view/components/QOfflineBanner.vue';

// Stores
import { useUiStore } from '@stores/ui';

// Personal libs
import { IUser } from '@shared/interface/model.interface';
import { logger } from '@shared/lib/logger/logger-manager';
import { ApiError } from '@shared/error/api.error';
import { displayDialogError } from '@shared/lib/notify.lib';
import { UserDto } from '@data/dto/user-dto';
import { RoleEnum } from '@shared/enum/role.enum';
import { UserStatusEnum } from '@shared/enum/user.enum';
import { UpdateManagerUser } from '@shared/service/updateManager/update-manager-user.service';
import { RepositoryStrategyEnum } from '@shared/enum/repository-strategy.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

/**
 * Stores
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'AdminUserPage',
  setup(__props) {

// Vendor
const uiStore = useUiStore();

/**
 * Reactive datas
 */
const loading = ref<boolean>(false);
const endLoading = ref<boolean>(false);
const selectedUser = ref<IUser>(new UserDto());
const showRolesDialog = ref(false);
const showDeleteUserDialog = ref(false);
const showCreateUserDialog = ref(false);
const showUpdateUserDialog = ref(false);
const showPasswordUserDialog = ref(false);
const showAcceptUserDialog = ref(false);
const showRejectUserDialog = ref(false);
const showResendUserValidationDialog = ref(false);

/**
 * Computed data
 */
const isOnline = computed(() => sessionManager.isOnline.value === true);
const users = computed<IUser[]>(() => uiStore.users ?? []);
const enabledUsers = computed<IUser[]>(() =>
  users.value
    .filter((u) => u.status === UserStatusEnum.ACTIVE)
    .sort((a, b) => (a.username ?? '').localeCompare(b.username ?? '')),
);
const validatingUsers = computed<IUser[]>(() =>
  users.value
    .filter((u) => u.status === UserStatusEnum.VALIDATING)
    .sort((a, b) => (a.username ?? '').localeCompare(b.username ?? '')),
);
const disabledUsers = computed<IUser[]>(() =>
  users.value
    .filter((u) => u.status === UserStatusEnum.INACTIVE)
    .sort((a, b) => (a.username ?? '').localeCompare(b.username ?? '')),
);
const userToValidate = computed<IUser[]>(() =>
  users.value
    .filter((u) => u.status === UserStatusEnum.VALIDATED)
    .sort((a, b) => (a.username ?? '').localeCompare(b.username ?? '')),
);

const isSuperAdmin = computed<boolean>(
  () =>
    sessionManager.currentUser.value?.roles?.includes(
      RoleEnum.ROLE_SUPER_ADMIN,
    ) === true,
);
const isAdmin = computed<boolean>(
  () =>
    sessionManager.currentUser.value?.roles?.includes(RoleEnum.ROLE_ADMIN) ===
    true,
);
const isUserValidator = computed<boolean>(
  () =>
    sessionManager.currentUser.value?.roles?.includes(
      RoleEnum.ROLE_USER_VALIDATE,
    ) === true,
);

/**
 * Methods
 */
function getSelectedUser(user: IUser) {
  selectedUser.value = user;
}

function getUsers() {
  loading.value = true;

  new UpdateManagerUser.UserAll()
    .exec(RepositoryStrategyEnum.NETWORK_FIRST)
    .catch((error) => {
      if (error instanceof ApiError) {
        displayDialogError({
          message: `Erreur dans la récupération des utilisateurs (${
            error.code
          }${error.message ? ': ' + error.message : ''})`,
        });
      } else {
        logger?.info(error.toString());
      }
    })
    .finally(() => {
      loading.value = false;
    });
}

function onUpdateSuccessful() {
  getUsers();
}

/**
 * Watch
 */
watch(
  () => loading.value,
  (newVal: boolean, oldVal: boolean) => {
    endLoading.value = oldVal && !newVal;
  },
);
/**
 * Hooks
 */
onBeforeMount(() => {
  getUsers();
});

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_spinner = _resolveComponent("q-spinner")!
  const _component_q_inner_loading = _resolveComponent("q-inner-loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(QPageTitle, { title: 'Admin: utilisateurs' }),
    _createElementVNode("div", _hoisted_1, [
      (isSuperAdmin.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_component_q_btn, {
              class: "q-mx-sm q-my-md set-outline",
              icon: 'add',
              label: "Ajouter un utilisateur",
              disable: !isOnline.value,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (showCreateUserDialog.value = true))
            }, null, 8 /* PROPS */, ["disable"])
          ]))
        : _createCommentVNode("v-if", true),
      _createElementVNode("div", null, [
        _createVNode(_component_q_card, { class: "q-mx-sm q-pa-xs" }, {
          default: _withCtx(() => [
            (isSuperAdmin.value || isAdmin.value)
              ? (_openBlock(), _createBlock(QAdminUserList, {
                  key: 0,
                  title: 'Utilisateurs actifs',
                  users: enabledUsers.value,
                  status: _unref(UserStatusEnum).ACTIVE,
                  onSelectedUser: getSelectedUser,
                  onShowUpdateDialog: _cache[1] || (_cache[1] = ($event: any) => (showUpdateUserDialog.value = true)),
                  onShowRolesDialog: _cache[2] || (_cache[2] = ($event: any) => (showRolesDialog.value = true)),
                  onShowDeleteDialog: _cache[3] || (_cache[3] = ($event: any) => (showDeleteUserDialog.value = true)),
                  onShowPasswordDialog: _cache[4] || (_cache[4] = ($event: any) => (showPasswordUserDialog.value = true))
                }, null, 8 /* PROPS */, ["users", "status"]))
              : _createCommentVNode("v-if", true),
            (isUserValidator.value || isSuperAdmin.value)
              ? (_openBlock(), _createBlock(QAdminUserList, {
                  key: 1,
                  title: 'Utilisateurs à valider',
                  users: userToValidate.value,
                  status: _unref(UserStatusEnum).VALIDATED,
                  onSelectedUser: getSelectedUser,
                  onShowAcceptDialog: _cache[5] || (_cache[5] = ($event: any) => (showAcceptUserDialog.value = true)),
                  onShowRejectDialog: _cache[6] || (_cache[6] = ($event: any) => (showRejectUserDialog.value = true))
                }, null, 8 /* PROPS */, ["users", "status"]))
              : _createCommentVNode("v-if", true),
            (isSuperAdmin.value || isAdmin.value)
              ? (_openBlock(), _createBlock(QAdminUserList, {
                  key: 2,
                  title: 'Utilisateurs en cours de validation',
                  users: validatingUsers.value,
                  status: _unref(UserStatusEnum).VALIDATING,
                  onSelectedUser: getSelectedUser,
                  onShowUpdateDialog: _cache[7] || (_cache[7] = ($event: any) => (showUpdateUserDialog.value = true)),
                  onShowDeleteDialog: _cache[8] || (_cache[8] = ($event: any) => (showDeleteUserDialog.value = true)),
                  onShowResendValidationLinkDialog: _cache[9] || (_cache[9] = ($event: any) => (showResendUserValidationDialog.value = true))
                }, null, 8 /* PROPS */, ["users", "status"]))
              : _createCommentVNode("v-if", true),
            (isSuperAdmin.value || isAdmin.value)
              ? (_openBlock(), _createBlock(QAdminUserList, {
                  key: 3,
                  title: 'Utilisateurs désactivés',
                  users: disabledUsers.value,
                  status: _unref(UserStatusEnum).INACTIVE,
                  onSelectedUser: getSelectedUser,
                  onShowUpdateDialog: _cache[10] || (_cache[10] = ($event: any) => (showUpdateUserDialog.value = true)),
                  onShowRolesDialog: _cache[11] || (_cache[11] = ($event: any) => (showRolesDialog.value = true)),
                  onShowDeleteDialog: _cache[12] || (_cache[12] = ($event: any) => (showDeleteUserDialog.value = true))
                }, null, 8 /* PROPS */, ["users", "status"]))
              : _createCommentVNode("v-if", true)
          ]),
          _: 1 /* STABLE */
        }),
        _createVNode(_component_q_inner_loading, {
          showing: loading.value,
          role: "alert",
          "aria-label": "Chargement des utilisateurs"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_spinner, {
              size: "100px",
              color: "primary"
            }),
            _cache[37] || (_cache[37] = _createElementVNode("span", { "aria-hidden": "true" }, "Chargement des utilisateurs, veuillez parienter...", -1 /* HOISTED */))
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["showing"]),
        (endLoading.value && !loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Chargement des utilisateurs terminé "))
          : _createCommentVNode("v-if", true)
      ]),
      _createCommentVNode(" Signup validation "),
      _createVNode(DialogUserAccept, {
        show: showAcceptUserDialog.value,
        username: selectedUser.value.username,
        uuid: selectedUser.value.uuid,
        onClose: _cache[13] || (_cache[13] = ($event: any) => (showAcceptUserDialog.value = false)),
        onFailed: _cache[14] || (_cache[14] = ($event: any) => (showAcceptUserDialog.value = false)),
        onSuccess: _cache[15] || (_cache[15] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show", "username", "uuid"]),
      _createCommentVNode(" Signup refusal "),
      _createVNode(DialogUserReject, {
        show: showRejectUserDialog.value,
        username: selectedUser.value.username,
        uuid: selectedUser.value.uuid,
        onClose: _cache[16] || (_cache[16] = ($event: any) => (showRejectUserDialog.value = false)),
        onFailed: _cache[17] || (_cache[17] = ($event: any) => (showRejectUserDialog.value = false)),
        onSuccess: _cache[18] || (_cache[18] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show", "username", "uuid"]),
      _createCommentVNode(" Signup resend the confirmation email "),
      _createVNode(DialogUserResendValidation, {
        show: showResendUserValidationDialog.value,
        username: selectedUser.value.username,
        uuid: selectedUser.value.uuid,
        onClose: _cache[19] || (_cache[19] = ($event: any) => (showResendUserValidationDialog.value = false)),
        onFailed: _cache[20] || (_cache[20] = ($event: any) => (showResendUserValidationDialog.value = false)),
        onSuccess: _cache[21] || (_cache[21] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show", "username", "uuid"]),
      _createCommentVNode(" Update roles "),
      _createVNode(DialogUserRoles, {
        show: showRolesDialog.value,
        user: selectedUser.value,
        onClose: _cache[22] || (_cache[22] = ($event: any) => (showRolesDialog.value = false)),
        onFailed: _cache[23] || (_cache[23] = ($event: any) => (showRolesDialog.value = false)),
        onSuccess: _cache[24] || (_cache[24] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show", "user"]),
      _createCommentVNode(" Add user "),
      _createVNode(DialogUserCreate, {
        show: showCreateUserDialog.value,
        onClose: _cache[25] || (_cache[25] = ($event: any) => (showCreateUserDialog.value = false)),
        onFailed: _cache[26] || (_cache[26] = ($event: any) => (showCreateUserDialog.value = false)),
        onSuccess: _cache[27] || (_cache[27] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show"]),
      _createCommentVNode(" Delete user "),
      _createVNode(DialogUserDelete, {
        show: showDeleteUserDialog.value,
        username: selectedUser.value.username,
        uuid: selectedUser.value.uuid,
        onClose: _cache[28] || (_cache[28] = ($event: any) => (showDeleteUserDialog.value = false)),
        onFailed: _cache[29] || (_cache[29] = ($event: any) => (showDeleteUserDialog.value = false)),
        onSuccess: _cache[30] || (_cache[30] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show", "username", "uuid"]),
      _createCommentVNode(" Update user "),
      _createVNode(DialogUserUpdate, {
        show: showUpdateUserDialog.value,
        user: selectedUser.value,
        onClose: _cache[31] || (_cache[31] = ($event: any) => (showUpdateUserDialog.value = false)),
        onFailed: _cache[32] || (_cache[32] = ($event: any) => (showUpdateUserDialog.value = false)),
        onSuccess: _cache[33] || (_cache[33] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show", "user"]),
      _createCommentVNode(" Create user "),
      _createVNode(DialogUserNewPassword, {
        show: showPasswordUserDialog.value,
        user: selectedUser.value,
        onClose: _cache[34] || (_cache[34] = ($event: any) => (showPasswordUserDialog.value = false)),
        onFailed: _cache[35] || (_cache[35] = ($event: any) => (showPasswordUserDialog.value = false)),
        onSuccess: _cache[36] || (_cache[36] = ($event: any) => (onUpdateSuccessful()))
      }, null, 8 /* PROPS */, ["show", "user"])
    ]),
    _createVNode(QOfflineBanner)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})